import { useState } from 'react';
import axios from 'axios';
import { isNil } from 'lodash';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type {
  GenericResponse,
  PolicyListResponse as ApiPolicyListResponse,
  PolicyRequest,
  PolicyResponse as ApiPolicyResponse,
  TemplateRequest,
  TemplateResponse,
} from '~/server_shared/openapi/data-contracts';

import type {
  PolicyCrudOperations,
  PolicyListResponse,
  PolicyModel,
  PolicyTemplateResponse,
} from '../types/policy.types';

// API hook for policy CRUD operations
export const usePolicyAPI = (): PolicyCrudOperations & {
  loading: boolean;
  error: string | null;
} => {
  const { organization } = useSysconfig() as { organization: { id: string } };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listPolicies = async (): Promise<PolicyListResponse> => {
    setLoading(true);
    setError(null);

    try {
      // Make API call to new backend endpoint with proper typing
      const response = await axios.get<ApiPolicyListResponse | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/policies`
      );

      // Check if response is an error
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      const apiResponse = response.data as ApiPolicyListResponse;
      // Convert API response to local type
      return {
        policies: apiResponse.policies as PolicyModel[],
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch policies';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getPolicy = async (policyUuid: string): Promise<PolicyModel> => {
    setLoading(true);
    setError(null);

    try {
      // Make API call to get specific policy with proper typing
      const response = await axios.get<ApiPolicyResponse | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/policies/${policyUuid}`
      );

      // Check if response is an error
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      const apiResponse = response.data as ApiPolicyResponse;
      // Convert API response to local type
      return apiResponse.policy as unknown as PolicyModel;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch policy';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createPolicy = async (policyRequest: PolicyRequest): Promise<PolicyModel> => {
    setLoading(true);
    setError(null);

    try {
      // Make API call to create policy with proper typing
      const response = await axios.post<ApiPolicyResponse | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/policies`,
        policyRequest
      );

      // Check if response is an error
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      const apiResponse = response.data as ApiPolicyResponse;
      // Convert API response to local type
      return apiResponse.policy as unknown as PolicyModel;
    } catch (err) {
      let errorMessage = 'Failed to create policy';

      // Handle axios errors with better message extraction
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data;

        // Extract error message from response data
        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (err.response?.status === 422) {
          errorMessage = 'Policy validation failed. Please check your policy data.';
        } else if (err.response?.status === 400) {
          errorMessage = responseData?.message || 'Invalid request. Please check your policy data.';
        } else if (err.response?.status && err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updatePolicy = async (policyUuid: string, policyRequest: PolicyRequest): Promise<PolicyModel> => {
    setLoading(true);
    setError(null);

    try {
      // Use PolicyRequest directly - no conversion needed

      // Make API call to update policy with proper typing
      const response = await axios.put<ApiPolicyResponse | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/policies/${policyUuid}`,
        policyRequest
      );

      // Check if response is an error
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      const apiResponse = response.data as ApiPolicyResponse;
      // Convert API response to local type
      return apiResponse.policy as unknown as PolicyModel;
    } catch (err) {
      let errorMessage = 'Failed to update policy';

      // Handle axios errors with better message extraction
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data;

        // Extract error message from response data
        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (err.response?.status === 422) {
          errorMessage = 'Policy validation failed. Please check your policy data.';
        } else if (err.response?.status === 400) {
          errorMessage = responseData?.message || 'Invalid request. Please check your policy data.';
        } else if (err.response?.status && err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message) {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deletePolicy = async (policyUuid: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Make API call to delete policy with proper typing
      await axios.delete<void | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/policies/${policyUuid}`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete policy';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getPolicyTemplate = async (request: TemplateRequest): Promise<PolicyTemplateResponse> => {
    setLoading(true);
    setError(null);

    try {
      // Build query parameters for template request
      const params = new URLSearchParams();
      params.append('lob', request.lob);
      if (!isNil(request.sub_organization_external_id)) {
        params.append('sub_organization_external_id', request.sub_organization_external_id);
      }

      // Make API call to get policy template with proper typing
      const response = await axios.get<TemplateResponse | GenericResponse>(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/templates?${params.toString()}`
      );

      // Check if response is an error
      if ('success' in response.data && !response.data.success) {
        const errorResponse = response.data as GenericResponse;
        throw new Error(errorResponse.message);
      }

      const apiResponse = response.data as TemplateResponse;
      // Convert API response to local type
      return {
        template: apiResponse.template as PolicyModel,
        lob: request.lob,
        sub_organization_external_id: request.sub_organization_external_id,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch template';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const rebuildIndex = async (): Promise<{ message: string; stats: Record<string, unknown> }> => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/sysconfig/api/v1/organizations/${organization.id}/mock_policy_server/rebuild_index`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to rebuild index';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      setError('An unexpected error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    listPolicies,
    getPolicy,
    createPolicy,
    updatePolicy,
    deletePolicy,
    getPolicyTemplate,
    rebuildIndex,
    loading,
    error,
  };
};
